/**
 * Type-safe API client with Zod validation
 * All functions are pure and side effects are isolated
 */
import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { z } from 'zod';
import type { AsyncResult } from '../../types/common';
import { ok, err } from '../../utils/fp-utils';

/**
 * API error type
 */
export type ApiError = Readonly<{
  message: string;
  status?: number;
  code?: string;
}>;

/**
 * Request configuration
 */
export type RequestConfig = Readonly<{
  signal?: AbortSignal;
  headers?: Record<string, string>;
}>;

/**
 * Creates an API error from an Axios error
 */
const createApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return {
      message: axiosError.response?.data?.message || axiosError.message,
      status: axiosError.response?.status,
      code: axiosError.code,
    };
  }
  
  if (error instanceof Error) {
    return {
      message: error.message,
    };
  }
  
  return {
    message: 'An unknown error occurred',
  };
};

/**
 * Creates the base axios instance
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: '/api',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Request interceptor for auth token
  instance.interceptors.request.use(
    (config) => {
      // TODO: Get token from auth context or memory
      // For now, check localStorage as fallback (document security risk)
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Response interceptor for token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // TODO: Implement token refresh logic here
      // If 401, try to refresh token, retry request
      return Promise.reject(error);
    }
  );
  
  return instance;
};

/**
 * Singleton axios instance
 */
const axiosInstance = createAxiosInstance();

/**
 * Generic request function with Zod validation
 */
export const request = async <T>(
  schema: z.ZodType<T>,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  data?: unknown,
  config?: RequestConfig
): AsyncResult<T, ApiError> => {
  try {
    const axiosConfig: AxiosRequestConfig = {
      method,
      url,
      data,
      signal: config?.signal,
      headers: config?.headers,
    };
    
    const response = await axiosInstance.request(axiosConfig);
    
    // Validate response with Zod
    const parseResult = schema.safeParse(response.data);
    
    if (!parseResult.success) {
      return err({
        message: 'Invalid response format from server',
        status: response.status,
      });
    }
    
    return ok(parseResult.data);
  } catch (error) {
    return err(createApiError(error));
  }
};

/**
 * GET request
 */
export const get = <T>(
  schema: z.ZodType<T>,
  url: string,
  config?: RequestConfig
): AsyncResult<T, ApiError> =>
  request(schema, 'GET', url, undefined, config);

/**
 * POST request
 */
export const post = <T>(
  schema: z.ZodType<T>,
  url: string,
  data?: unknown,
  config?: RequestConfig
): AsyncResult<T, ApiError> =>
  request(schema, 'POST', url, data, config);

/**
 * PUT request
 */
export const put = <T>(
  schema: z.ZodType<T>,
  url: string,
  data?: unknown,
  config?: RequestConfig
): AsyncResult<T, ApiError> =>
  request(schema, 'PUT', url, data, config);

/**
 * PATCH request
 */
export const patch = <T>(
  schema: z.ZodType<T>,
  url: string,
  data?: unknown,
  config?: RequestConfig
): AsyncResult<T, ApiError> =>
  request(schema, 'PATCH', url, data, config);

/**
 * DELETE request
 */
export const del = <T>(
  schema: z.ZodType<T>,
  url: string,
  config?: RequestConfig
): AsyncResult<T, ApiError> =>
  request(schema, 'DELETE', url, undefined, config);

/**
 * Creates an AbortController for request cancellation
 */
export const createAbortController = (): AbortController =>
  new AbortController();

/**
 * Higher-order function to add retry logic
 */
export const withRetry = <Args extends any[], T>(
  fn: (...args: Args) => AsyncResult<T, ApiError>,
  maxAttempts: number = 3,
  delayMs: number = 1000
) => async (...args: Args): AsyncResult<T, ApiError> => {
  let lastError: ApiError;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const result = await fn(...args);
    
    if (result.success) {
      return result;
    }
    
    lastError = result.error;
    
    // Don't retry on client errors (4xx)
    if (lastError.status && lastError.status >= 400 && lastError.status < 500) {
      return result;
    }
    
    if (attempt < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
    }
  }
  
  return err(lastError!);
};

/**
 * Type guard for API error
 */
export const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as ApiError).message === 'string';
